import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  CardBody,
  Col,
  Container,
  FormGroup,
  Input,
  Label,
  ListGroup,
  ListGroupItem,
  Progress,
  Row,
  Spinner,
  Table,
} from "reactstrap";
import Select from "react-select";
import { useTranslation } from "react-i18next";
import { toast, ToastContainer } from "react-toastify";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import DismissiblePageTip from "../../../Components/Common/DismissiblePageTip";
import PageHelpLink from "../../../Components/Common/PageHelpLink";
import { helpUrlForRoute } from "../../../constants/helpRouteMap";
import {
  AbandonedCartAutomation,
  apiErrorMessage,
  downloadAbandonedCartCsv,
  getAbandonedCartAutomation,
  testAbandonedCartAutomation,
  updateAbandonedCartAutomation,
} from "../../../helpers/automations_helper";
import { Campaign, getCampaigns } from "../../../helpers/campaigns_helper";
import { getInstalledIntegrations } from "../../../helpers/integrations_helper";
import {
  formatInTenantTimezone,
  readDisplayTimezone,
} from "../../../helpers/tenantTimezone";
import { Link, Navigate, useSearchParams } from "react-router-dom";

type CampaignOption = { value: number; label: string };
type ExportMode = "all" | "converted" | "cancelled" | "today";

/**
 * Carrinho abandonado: opt-in + cooldown + métricas + teste (DOC 03 § 4.14).
 * Menu e rota só fazem sentido com app ecommerce.sync ativo.
 */
const AbandonedCartAutomationPage = () => {
  const { t, i18n } = useTranslation();
  document.title = t("automations.abandonedCart.documentTitle");

  const helpHref = helpUrlForRoute("/automations/abandoned-cart");
  const tz = readDisplayTimezone();
  const [searchParams] = useSearchParams();
  const todayOnly = searchParams.get("today") === "1";

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [config, setConfig] = useState<AbandonedCartAutomation | null>(null);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [enabled, setEnabled] = useState(false);
  const [campaignId, setCampaignId] = useState<number | null>(null);
  const [delayMinutes, setDelayMinutes] = useState(0);
  const [cooldownHours, setCooldownHours] = useState(24);
  const [dripDelayMinutes, setDripDelayMinutes] = useState(0);
  const [dripCampaignId, setDripCampaignId] = useState<number | null>(null);
  const [dripAbEnabled, setDripAbEnabled] = useState(false);
  const [storeConnected, setStoreConnected] = useState(false);
  const [gateChecked, setGateChecked] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [exporting, setExporting] = useState(false);
  const [exportMode, setExportMode] = useState<ExportMode>("all");
  const [showHtmlPreview, setShowHtmlPreview] = useState(false);
  const [previewViewport, setPreviewViewport] = useState<"desktop" | "mobile">(
    "desktop"
  );

  const applyConfig = useCallback(
    (auto: AbandonedCartAutomation, showWarnings = false) => {
      setConfig(auto);
      setEnabled(!!auto.enabled);
      setCampaignId(auto.campaign_id);
      setDelayMinutes(auto.delay_minutes ?? 0);
      setCooldownHours(auto.cooldown_hours ?? 24);
      setDripDelayMinutes(auto.drip_delay_minutes ?? 0);
      setDripCampaignId(auto.drip_campaign_id ?? null);
      setDripAbEnabled(!!auto.drip_ab_enabled);
      if (showWarnings) {
        (auto.warnings || []).forEach((w) => toast.warn(w));
      }
      // Aviso único por sessão quando houve envio recente (notificação no painel).
      const sent24 = auto.metrics?.sent_last_24h ?? 0;
      if (sent24 > 0) {
        const key = `abandoned-sent-toast:${auto.metrics?.last_sent_at || sent24}`;
        if (!sessionStorage.getItem(key)) {
          sessionStorage.setItem(key, "1");
          toast.info(
            t("automations.abandonedCart.sentRecentToast", { count: sent24 })
          );
        }
      }
    },
    [t]
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const installed = await getInstalledIntegrations();
      const hasStore = (installed?.data || []).some(
        (r) => r.enabled && r.capability === "ecommerce.sync"
      );
      setStoreConnected(hasStore);
      setGateChecked(true);
      if (!hasStore) {
        return;
      }

      const [autoRes, list] = await Promise.all([
        getAbandonedCartAutomation({ today: todayOnly }),
        getCampaigns({ has_html: true, per_page: 100 }),
      ]);
      applyConfig(autoRes.data, false);
      setCampaigns(list.data || []);
    } catch (e) {
      setGateChecked(true);
      setError(apiErrorMessage(e, t("automations.abandonedCart.loadError")));
    } finally {
      setLoading(false);
    }
  }, [t, todayOnly, applyConfig]);

  useEffect(() => {
    void load();
  }, [load]);

  const options: CampaignOption[] = useMemo(
    () =>
      campaigns.map((c) => ({
        value: c.id,
        label: `${c.name} — ${c.subject || t("automations.abandonedCart.noSubject")}`,
      })),
    [campaigns, t]
  );

  const selected = options.find((o) => o.value === campaignId) || null;
  const selectedDrip = options.find((o) => o.value === dripCampaignId) || null;
  const metrics = config?.metrics;
  const recent = config?.recent_events || [];
  const cancelled = config?.recent_cancelled || [];

  const onExport = async () => {
    setExporting(true);
    try {
      await downloadAbandonedCartCsv({
        converted: exportMode === "converted",
        cancelled: exportMode === "cancelled",
        today: exportMode === "today" || todayOnly,
        days: 90,
      });
      toast.success(t("automations.abandonedCart.exportOk"));
    } catch (e) {
      toast.error(apiErrorMessage(e, t("automations.abandonedCart.exportError")));
    } finally {
      setExporting(false);
    }
  };

  // Prévia do 2º e-mail: mesma campanha se drip_campaign_id vazio.
  const dripPreviewCampaign = useMemo(() => {
    if (dripDelayMinutes <= 0) return null;
    const id = dripCampaignId ?? campaignId;
    if (id == null) return null;
    return campaigns.find((c) => c.id === id) ?? null;
  }, [dripDelayMinutes, dripCampaignId, campaignId, campaigns]);

  const dripPreviewSnippet = useMemo(() => {
    if (!dripPreviewCampaign?.html_body) return "";
    return dripPreviewCampaign.html_body
      .replace(/<style[\s\S]*?<\/style>/gi, " ")
      .replace(/<[^>]+>/g, " ")
      .replace(/\s+/g, " ")
      .trim()
      .slice(0, 160);
  }, [dripPreviewCampaign]);

  const conversionPercent = Math.round((metrics?.conversion_bps ?? 0) / 100);
  const conversionNot =
    Math.max(0, (metrics?.events_total ?? 0) - (metrics?.converted ?? 0));

  const onSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const res = await updateAbandonedCartAutomation({
        enabled,
        campaign_id: campaignId,
        delay_minutes: delayMinutes,
        cooldown_hours: cooldownHours,
        drip_delay_minutes: dripDelayMinutes,
        drip_campaign_id: dripCampaignId,
        drip_ab_enabled: dripAbEnabled,
      });
      applyConfig(res.data, true);
      toast.success(t("automations.abandonedCart.saved"));
    } catch (e) {
      setError(apiErrorMessage(e, t("automations.abandonedCart.saveError")));
    } finally {
      setSaving(false);
    }
  };

  const onTest = async () => {
    setTesting(true);
    try {
      await testAbandonedCartAutomation(testEmail.trim());
      toast.success(t("automations.abandonedCart.testOk"));
      const refreshed = await getAbandonedCartAutomation();
      applyConfig(refreshed.data, false);
    } catch (e) {
      toast.error(apiErrorMessage(e, t("automations.abandonedCart.testError")));
    } finally {
      setTesting(false);
    }
  };

  const statusLabel = (status: string) =>
    t(`automations.abandonedCart.eventStatus.${status}`, {
      defaultValue: status,
    });

  const skipReasonLabel = (reason: string | null | undefined) => {
    if (!reason) return "";
    return t(`automations.abandonedCart.skip.${reason}`, {
      defaultValue: t("automations.abandonedCart.skip.generic"),
    });
  };

  // URL direta sem loja → Hub (menu já esconde o item).
  if (gateChecked && !storeConnected) {
    return <Navigate to="/integrations" replace />;
  }

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title={t("automations.abandonedCart.title")}
          pageTitle={t("menu.automation")}
          actions={
            helpHref ? (
              <PageHelpLink
                href={helpHref}
                label={t("automations.abandonedCart.help")}
              />
            ) : undefined
          }
        />
        <DismissiblePageTip tipId="abandoned-cart-automation-intro">
          {t("automations.abandonedCart.tip")}
        </DismissiblePageTip>

        {error && (
          <Alert color="danger" toggle={() => setError(null)}>
            {error}
          </Alert>
        )}

        {todayOnly && (
          <Alert color="info" className="py-2">
            {t("automations.abandonedCart.todayFilterBanner")}{" "}
            <Link to="/automations/abandoned-cart">
              {t("automations.abandonedCart.todayFilterClear")}
            </Link>
          </Alert>
        )}

        {!loading &&
          config &&
          !enabled &&
          (metrics?.events_today ?? 0) >= 3 && (
            <Alert color="warning">
              {t("automations.abandonedCart.automationOffHighVolume", {
                count: metrics?.events_today ?? 0,
              })}
            </Alert>
          )}

        <Row>
          <Col lg={8}>
            <Card>
              <CardBody>
                {loading || !config ? (
                  <div className="text-center py-5">
                    <Spinner color="primary" />
                  </div>
                ) : (
                  <>
                    <ListGroup flush className="mb-4 border rounded">
                      <ListGroupItem className="d-flex align-items-center gap-2">
                        <i
                          className={`ri-checkbox-circle-fill ${
                            storeConnected ? "text-success" : "text-muted"
                          }`}
                        />
                        <span>
                          {t("automations.abandonedCart.check.store")}{" "}
                          {!storeConnected && (
                            <Link to="/integrations">
                              {t("automations.abandonedCart.check.storeCta")}
                            </Link>
                          )}
                        </span>
                      </ListGroupItem>
                      <ListGroupItem className="d-flex align-items-center gap-2">
                        <i
                          className={`ri-checkbox-circle-fill ${
                            campaignId ? "text-success" : "text-muted"
                          }`}
                        />
                        <span>
                          {t("automations.abandonedCart.check.campaign")}
                        </span>
                      </ListGroupItem>
                      <ListGroupItem className="d-flex align-items-center gap-2">
                        <i
                          className={`ri-checkbox-circle-fill ${
                            enabled ? "text-success" : "text-muted"
                          }`}
                        />
                        <span>
                          {t("automations.abandonedCart.check.enable")}
                        </span>
                      </ListGroupItem>
                      <ListGroupItem className="d-flex align-items-center gap-2">
                        <i
                          className={`ri-checkbox-circle-fill ${
                            enabled && storeConnected && recent.length > 0
                              ? "text-success"
                              : "text-muted"
                          }`}
                        />
                        <span>
                          {t("automations.abandonedCart.check.test")}
                        </span>
                      </ListGroupItem>
                    </ListGroup>

                    <FormGroup switch className="mb-4">
                      <Input
                        type="switch"
                        role="switch"
                        id="abandoned-enabled"
                        checked={enabled}
                        disabled={saving}
                        onChange={(e) => setEnabled(e.target.checked)}
                      />
                      <Label check for="abandoned-enabled" className="ms-2">
                        {t("automations.abandonedCart.enabledLabel")}
                      </Label>
                      <p className="text-muted small mb-0 mt-1">
                        {t("automations.abandonedCart.enabledHelp")}
                      </p>
                    </FormGroup>

                    <FormGroup className="mb-3">
                      <Label>{t("automations.abandonedCart.campaignLabel")}</Label>
                      <Select
                        options={options}
                        value={selected}
                        onChange={(opt: CampaignOption | null) =>
                          setCampaignId(opt ? opt.value : null)
                        }
                        isClearable
                        isDisabled={saving}
                        placeholder={t(
                          "automations.abandonedCart.campaignPlaceholder"
                        )}
                        classNamePrefix="select"
                        noOptionsMessage={() =>
                          t("automations.abandonedCart.noCampaigns")
                        }
                      />
                      <p className="text-muted small mb-0 mt-1">
                        {t("automations.abandonedCart.campaignHelp")}
                      </p>
                    </FormGroup>

                    <FormGroup className="mb-3">
                      <Label for="abandoned-delay">
                        {t("automations.abandonedCart.delayLabel")}
                      </Label>
                      <Input
                        id="abandoned-delay"
                        type="number"
                        min={0}
                        max={10080}
                        value={delayMinutes}
                        disabled={saving}
                        onChange={(e) =>
                          setDelayMinutes(
                            Math.max(
                              0,
                              Math.min(10080, Number(e.target.value) || 0)
                            )
                          )
                        }
                      />
                      <p className="text-muted small mb-0 mt-1">
                        {t("automations.abandonedCart.delayHelp")}
                      </p>
                    </FormGroup>

                    <FormGroup className="mb-4">
                      <Label for="abandoned-cooldown">
                        {t("automations.abandonedCart.cooldownLabel")}
                      </Label>
                      <Input
                        id="abandoned-cooldown"
                        type="number"
                        min={1}
                        max={720}
                        value={cooldownHours}
                        disabled={saving}
                        onChange={(e) =>
                          setCooldownHours(
                            Math.max(1, Math.min(720, Number(e.target.value) || 24))
                          )
                        }
                      />
                      <p className="text-muted small mb-0 mt-1">
                        {t("automations.abandonedCart.cooldownHelp")}
                      </p>
                    </FormGroup>

                    <FormGroup className="mb-3">
                      <Label for="abandoned-drip">
                        {t("automations.abandonedCart.dripDelayLabel")}
                      </Label>
                      <Input
                        id="abandoned-drip"
                        type="number"
                        min={0}
                        max={10080}
                        value={dripDelayMinutes}
                        disabled={saving}
                        onChange={(e) =>
                          setDripDelayMinutes(
                            Math.max(
                              0,
                              Math.min(10080, Number(e.target.value) || 0)
                            )
                          )
                        }
                      />
                      <p className="text-muted small mb-0 mt-1">
                        {t("automations.abandonedCart.dripDelayHelp")}
                      </p>
                    </FormGroup>

                    {dripDelayMinutes > 0 && (
                      <>
                        <FormGroup className="mb-3">
                          <Label>
                            {t("automations.abandonedCart.dripCampaignLabel")}
                          </Label>
                          <Select
                            options={options}
                            value={selectedDrip}
                            onChange={(opt: CampaignOption | null) =>
                              setDripCampaignId(opt ? opt.value : null)
                            }
                            isClearable
                            isDisabled={saving}
                            placeholder={t(
                              "automations.abandonedCart.dripCampaignPlaceholder"
                            )}
                            classNamePrefix="select"
                          />
                          <p className="text-muted small mb-0 mt-1">
                            {t("automations.abandonedCart.dripCampaignHelp")}
                          </p>
                        </FormGroup>

                        <FormGroup switch className="mb-3">
                          <Input
                            type="switch"
                            role="switch"
                            id="abandoned-drip-ab"
                            checked={dripAbEnabled}
                            disabled={saving}
                            onChange={(e) => setDripAbEnabled(e.target.checked)}
                          />
                          <Label check for="abandoned-drip-ab" className="ms-2">
                            {t("automations.abandonedCart.dripAbLabel")}
                          </Label>
                          <p className="text-muted small mb-0 mt-1">
                            {t("automations.abandonedCart.dripAbHelp")}
                          </p>
                        </FormGroup>

                        <Alert color="light" className="mb-4 border">
                          <div className="fw-medium mb-1">
                            {t("automations.abandonedCart.dripPreviewTitle")}
                          </div>
                          <p className="small mb-2 text-muted">
                            {t("automations.abandonedCart.dripPreviewTiming", {
                              first: delayMinutes,
                              second: delayMinutes + dripDelayMinutes,
                            })}
                          </p>
                          {dripPreviewCampaign ? (
                            <>
                              <div className="small mb-1">
                                <strong>
                                  {t("automations.abandonedCart.dripPreviewSubject")}
                                </strong>{" "}
                                {dripPreviewCampaign.subject ||
                                  t("automations.abandonedCart.noSubject")}
                              </div>
                              {dripPreviewSnippet ? (
                                <p className="small text-muted mb-2">
                                  {dripPreviewSnippet}
                                  {dripPreviewSnippet.length >= 160 ? "…" : ""}
                                </p>
                              ) : null}
                              <div className="d-flex flex-wrap gap-2 align-items-center mb-2">
                                <Button
                                  color="link"
                                  size="sm"
                                  className="p-0"
                                  type="button"
                                  onClick={() => setShowHtmlPreview((v) => !v)}
                                >
                                  {showHtmlPreview
                                    ? t(
                                        "automations.abandonedCart.dripPreviewHideHtml"
                                      )
                                    : t(
                                        "automations.abandonedCart.dripPreviewShowHtml"
                                      )}
                                </Button>
                                {showHtmlPreview && (
                                  <>
                                    <Button
                                      color={
                                        previewViewport === "desktop"
                                          ? "secondary"
                                          : "link"
                                      }
                                      size="sm"
                                      type="button"
                                      className={
                                        previewViewport === "desktop"
                                          ? "py-0"
                                          : "p-0"
                                      }
                                      onClick={() =>
                                        setPreviewViewport("desktop")
                                      }
                                    >
                                      {t(
                                        "automations.abandonedCart.previewDesktop"
                                      )}
                                    </Button>
                                    <Button
                                      color={
                                        previewViewport === "mobile"
                                          ? "secondary"
                                          : "link"
                                      }
                                      size="sm"
                                      type="button"
                                      className={
                                        previewViewport === "mobile"
                                          ? "py-0"
                                          : "p-0"
                                      }
                                      onClick={() =>
                                        setPreviewViewport("mobile")
                                      }
                                    >
                                      {t(
                                        "automations.abandonedCart.previewMobile"
                                      )}
                                    </Button>
                                  </>
                                )}
                                <Link
                                  to={`/campaigns/${dripPreviewCampaign.id}/edit`}
                                  className="small"
                                >
                                  {t("automations.abandonedCart.dripPreviewEdit")}
                                </Link>
                              </div>
                              {showHtmlPreview &&
                              dripPreviewCampaign.html_body ? (
                                <div className="d-flex justify-content-center">
                                  <iframe
                                    title={t(
                                      "automations.abandonedCart.dripPreviewHtmlTitle"
                                    )}
                                    sandbox=""
                                    srcDoc={dripPreviewCampaign.html_body}
                                    className="border rounded"
                                    style={{
                                      height: 220,
                                      background: "#fff",
                                      width:
                                        previewViewport === "mobile"
                                          ? 375
                                          : "100%",
                                      maxWidth: "100%",
                                    }}
                                  />
                                </div>
                              ) : null}
                            </>
                          ) : (
                            <p className="small text-muted mb-0">
                              {t(
                                "automations.abandonedCart.dripPreviewNeedCampaign"
                              )}
                            </p>
                          )}
                        </Alert>
                      </>
                    )}

                    <Button
                      color="primary"
                      disabled={saving}
                      onClick={() => void onSave()}
                    >
                      {saving ? (
                        <Spinner size="sm" className="me-1" />
                      ) : null}
                      {t("automations.abandonedCart.save")}
                    </Button>

                    {enabled && storeConnected && (
                      <div className="border-top mt-4 pt-4">
                        <h6 className="mb-2">
                          {t("automations.abandonedCart.testTitle")}
                        </h6>
                        <p className="text-muted small">
                          {t("automations.abandonedCart.testHelp")}
                        </p>
                        <div className="d-flex flex-wrap gap-2 align-items-end">
                          <FormGroup className="mb-0 flex-grow-1" style={{ minWidth: 220 }}>
                            <Label for="test-email">
                              {t("automations.abandonedCart.testEmail")}
                            </Label>
                            <Input
                              id="test-email"
                              type="email"
                              value={testEmail}
                              onChange={(e) => setTestEmail(e.target.value)}
                              disabled={testing}
                            />
                          </FormGroup>
                          <Button
                            color="secondary"
                            outline
                            disabled={testing || !testEmail.trim()}
                            onClick={() => void onTest()}
                          >
                            {testing && <Spinner size="sm" className="me-1" />}
                            {t("automations.abandonedCart.testCta")}
                          </Button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </CardBody>
            </Card>

            <Card className="mt-3">
              <CardBody>
                <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                  <h5 className="card-title mb-0">
                    {t("automations.abandonedCart.recentTitle")}
                    {todayOnly
                      ? ` — ${t("automations.abandonedCart.recentTodaySuffix")}`
                      : ""}
                  </h5>
                  <div className="d-flex flex-wrap gap-2 align-items-center">
                    <Input
                      type="select"
                      bsSize="sm"
                      style={{ width: "auto" }}
                      value={exportMode}
                      disabled={exporting}
                      onChange={(e) =>
                        setExportMode(e.target.value as ExportMode)
                      }
                      aria-label={t("automations.abandonedCart.exportFilterLabel")}
                    >
                      <option value="all">
                        {t("automations.abandonedCart.exportFilterAll")}
                      </option>
                      <option value="converted">
                        {t("automations.abandonedCart.exportFilterConverted")}
                      </option>
                      <option value="cancelled">
                        {t("automations.abandonedCart.exportFilterCancelled")}
                      </option>
                      <option value="today">
                        {t("automations.abandonedCart.exportFilterToday")}
                      </option>
                    </Input>
                    <Button
                      color="secondary"
                      outline
                      size="sm"
                      type="button"
                      disabled={exporting}
                      onClick={() => void onExport()}
                    >
                      {exporting ? (
                        <Spinner size="sm" className="me-1" />
                      ) : null}
                      {t("automations.abandonedCart.exportCsv")}
                    </Button>
                  </div>
                </div>
                {recent.length === 0 ? (
                  <p className="text-muted mb-0">
                    {t("automations.abandonedCart.recentEmpty")}
                  </p>
                ) : (
                  <div className="table-responsive">
                    <Table className="table-sm align-middle mb-0">
                      <thead>
                        <tr>
                          <th>{t("automations.abandonedCart.col.when")}</th>
                          <th>{t("automations.abandonedCart.col.email")}</th>
                          <th>{t("automations.abandonedCart.col.status")}</th>
                          <th>{t("automations.abandonedCart.col.order")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {recent.map((ev) => (
                          <tr key={ev.id}>
                            <td className="text-nowrap">
                              {formatInTenantTimezone(
                                ev.created_at,
                                tz,
                                i18n.language
                              )}
                            </td>
                            <td>{ev.email}</td>
                            <td>
                              {statusLabel(ev.status)}
                              {ev.skip_reason
                                ? ` — ${skipReasonLabel(ev.skip_reason)}`
                                : ""}
                              {ev.drip_ab_bucket
                                ? ` · A/B ${ev.drip_ab_bucket.toUpperCase()}`
                                : ""}
                            </td>
                            <td className="text-nowrap small">
                              {ev.converted_order_id || "—"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </CardBody>
            </Card>

            {cancelled.length > 0 && (
              <Card className="mt-3">
                <CardBody>
                  <h5 className="card-title mb-3">
                    {t("automations.abandonedCart.cancelledTitle")}
                  </h5>
                  <p className="text-muted small">
                    {t("automations.abandonedCart.cancelledHelp")}
                  </p>
                  <ul className="list-unstyled mb-0 small">
                    {cancelled.map((row) => (
                      <li key={row.id} className="mb-2">
                        <strong>{row.email}</strong>
                        {" — "}
                        {formatInTenantTimezone(
                          row.cancelled_at,
                          tz,
                          i18n.language
                        )}
                      </li>
                    ))}
                  </ul>
                </CardBody>
              </Card>
            )}
          </Col>

          <Col lg={4}>
            {metrics && (
              <Card>
                <CardBody>
                  <h5 className="card-title mb-3">
                    {t("automations.abandonedCart.metricsTitle")}
                  </h5>

                  {(metrics.sent_last_24h ?? 0) > 0 && (
                    <Alert color="success" className="py-2 small">
                      {t("automations.abandonedCart.sentRecentBanner", {
                        count: metrics.sent_last_24h,
                      })}
                    </Alert>
                  )}

                  {metrics.drip_paused_complaint && (
                    <Alert color="warning" className="py-2 small">
                      {t("automations.abandonedCart.dripPausedBanner")}
                    </Alert>
                  )}

                  <div className="mb-4">
                    <div className="d-flex justify-content-between small mb-1">
                      <span>
                        {t("automations.abandonedCart.conversionChartTitle")}
                      </span>
                      <span className="fw-medium">{conversionPercent}%</span>
                    </div>
                    <Progress
                      value={conversionPercent}
                      color="success"
                      className="mb-2"
                      style={{ height: "0.65rem" }}
                    />
                    <div className="d-flex justify-content-between small text-muted">
                      <span>
                        {t("automations.abandonedCart.conversionChartConverted", {
                          count: metrics.converted ?? 0,
                        })}
                      </span>
                      <span>
                        {t("automations.abandonedCart.conversionChartOpen", {
                          count: conversionNot,
                        })}
                      </span>
                    </div>
                  </div>

                  {((metrics.events_with_drip ?? 0) > 0 ||
                    (metrics.events_without_drip ?? 0) > 0) && (
                    <div className="mb-4 small border rounded p-2">
                      <div className="fw-medium mb-1">
                        {t("automations.abandonedCart.dripCompareTitle")}
                      </div>
                      <div className="text-muted">
                        {t("automations.abandonedCart.dripCompareWith", {
                          percent: Math.round(
                            (metrics.conversion_with_drip_bps ?? 0) / 100
                          ),
                          count: metrics.events_with_drip ?? 0,
                        })}
                      </div>
                      <div className="text-muted">
                        {t("automations.abandonedCart.dripCompareWithout", {
                          percent: Math.round(
                            (metrics.conversion_without_drip_bps ?? 0) / 100
                          ),
                          count: metrics.events_without_drip ?? 0,
                        })}
                      </div>
                    </div>
                  )}

                  {(metrics.sparkline_7d?.length ?? 0) > 0 && (
                    <div className="mb-4">
                      <div className="small fw-medium mb-2">
                        {t("automations.abandonedCart.sparklineTitle")}
                      </div>
                      <div
                        className="d-flex align-items-end gap-1"
                        style={{ height: 56 }}
                        aria-hidden
                      >
                        {(() => {
                          const max = Math.max(
                            1,
                            ...(metrics.sparkline_7d || []).map((p) =>
                              Math.max(p.events, p.converted)
                            )
                          );
                          return (metrics.sparkline_7d || []).map((p) => (
                            <div
                              key={p.date}
                              className="flex-fill d-flex flex-column justify-content-end align-items-center"
                              title={`${p.date}: ${p.events} / ${p.converted}`}
                              style={{ minWidth: 0 }}
                            >
                              <div
                                className="w-100 rounded-top bg-primary-subtle"
                                style={{
                                  height: `${Math.round(
                                    (p.events / max) * 100
                                  )}%`,
                                  minHeight: p.events > 0 ? 4 : 1,
                                }}
                              />
                              <div
                                className="w-50 rounded-top bg-success"
                                style={{
                                  height: `${Math.round(
                                    (p.converted / max) * 100
                                  )}%`,
                                  minHeight: p.converted > 0 ? 3 : 0,
                                  marginTop: 1,
                                }}
                              />
                            </div>
                          ));
                        })()}
                      </div>
                      <p className="text-muted small mb-0 mt-1">
                        {t("automations.abandonedCart.sparklineHelp")}
                      </p>
                    </div>
                  )}

                  <ul className="list-unstyled mb-0">
                    <li className="mb-2">
                      {t("automations.abandonedCart.metric.eventsToday", {
                        count: metrics.events_today,
                      })}
                    </li>
                    <li className="mb-2">
                      {t("automations.abandonedCart.metric.eventsTotal", {
                        count: metrics.events_total,
                      })}
                    </li>
                    <li className="mb-2">
                      {t("automations.abandonedCart.metric.pending", {
                        count: metrics.pending,
                      })}
                    </li>
                    <li className="mb-2">
                      {t("automations.abandonedCart.metric.sent", {
                        count: metrics.sent,
                      })}
                    </li>
                    <li className="mb-2">
                      {t("automations.abandonedCart.metric.failed", {
                        count: metrics.failed,
                      })}
                    </li>
                    <li className="mb-0">
                      {t("automations.abandonedCart.metric.skipped", {
                        count: metrics.skipped,
                      })}
                    </li>
                    <li className="mt-2 mb-0">
                      {t("automations.abandonedCart.metric.converted", {
                        count: metrics.converted ?? 0,
                        percent: conversionPercent,
                      })}
                    </li>
                    {(metrics.cancelled_last_24h ?? 0) > 0 && (
                      <li className="mt-2 mb-0 text-muted">
                        {t("automations.abandonedCart.metric.cancelled24h", {
                          count: metrics.cancelled_last_24h,
                        })}
                      </li>
                    )}
                  </ul>
                  <p className="text-muted small mt-3 mb-0">
                    {t("automations.abandonedCart.metricsNote")}
                  </p>
                </CardBody>
              </Card>
            )}
          </Col>
        </Row>
      </Container>
      <ToastContainer />
    </div>
  );
};

export default AbandonedCartAutomationPage;
